const toCurrencyCents = value =>
  Math.round((Number(value) + Number.EPSILON) * 100);

const fromCurrencyCents = value => value / 100;

const getDefaultCreditAllocation = ({ availableAmount = 0 }) =>
  Math.max(toCurrencyCents(availableAmount), 0) / 100;

const buildCreditRow = ({
  amount = 0,
  description = "",
  id = null,
  nominalId = null,
  number,
  remainingAmount = null,
}) => ({
  [`id_${number}`]: id,
  [`nominalId_${number}`]: nominalId,
  [`description_${number}`]: description,
  [`amount_${number}`]: amount,
  [`remainingAmount_${number}`]: remainingAmount,
  [`action_${number}`]: "removeDynamicInputs",
});

const getCreditAllSourceItems = (invoiceItems = []) => {
  const sales = invoiceItems.filter(item => item?.typeId === 8);

  return sales.filter(
    (item, index) =>
      sales.findIndex(candidate => {
        if (item?.instanceId) {
          return candidate?.instanceId === item.instanceId;
        }
        if (item?.catalogueId) {
          return candidate?.catalogueId === item.catalogueId;
        }
        return candidate?.id === item?.id;
      }) === index,
  );
};

const allocateAvailableInvoiceBalance = ({
  availableBalance,
  creditItems = [],
}) => {
  const sources = creditItems
    .map(({ details, id }) => {
      const remainingAmount =
        details?.remainingCreditAmount ?? details?.amount ?? 0;
      return {
        details,
        id,
        remainingAmount,
        remainingCents: Math.max(toCurrencyCents(remainingAmount), 0),
        taxMultiplier: Math.max(
          Number(details?.creditTaxMultiplier) || 0,
          0,
        ),
      };
    })
    .filter(item => item.remainingCents > 0);
  const totalRemainingCents = sources.reduce(
    (total, source) => total + source.remainingCents,
    0,
  );
  const availableBalanceCents = Math.max(toCurrencyCents(availableBalance), 0);

  const allocationsForTarget = targetCents => {
    const allocated = sources.map((source, index) => {
      const weightedAmount = targetCents * source.remainingCents;
      return {
        ...source,
        index,
        amountCents: Math.floor(weightedAmount / totalRemainingCents),
        remainder: weightedAmount % totalRemainingCents,
      };
    });
    const allocatedCents = allocated.reduce(
      (total, allocation) => total + allocation.amountCents,
      0,
    );
    const extraIndexes = new Set(
      [...allocated]
        .sort(
          (first, second) =>
            second.remainder - first.remainder || first.index - second.index,
        )
        .slice(0, targetCents - allocatedCents)
        .map(allocation => allocation.index),
    );
    return allocated.map(allocation => ({
      ...allocation,
      amountCents:
        allocation.amountCents +
        (extraIndexes.has(allocation.index) ? 1 : 0),
    }));
  };
  const projectedReduction = allocations =>
    allocations.reduce(
      (total, allocation) =>
        total +
        allocation.amountCents +
        toCurrencyCents(
          fromCurrencyCents(allocation.amountCents) *
            allocation.taxMultiplier,
        ),
      0,
    );

  if (!availableBalanceCents || !totalRemainingCents) return [];

  const getMaximumTarget = (lowerBound, upperBound) => {
    if (lowerBound >= upperBound) return lowerBound;
    const candidate = Math.ceil((lowerBound + upperBound) / 2);
    return projectedReduction(allocationsForTarget(candidate)) <=
      availableBalanceCents
      ? getMaximumTarget(candidate, upperBound)
      : getMaximumTarget(lowerBound, candidate - 1);
  };

  return allocationsForTarget(getMaximumTarget(0, totalRemainingCents)).filter(
    allocation => allocation.amountCents > 0,
  );
};

const buildCreditAllRows = ({ availableBalance, creditItems = [] }) =>
  allocateAvailableInvoiceBalance({
    availableBalance,
    creditItems,
  }).map(({ amountCents, details, id, remainingAmount }, index) =>
    buildCreditRow({
      amount: fromCurrencyCents(amountCents),
      description: details?.description,
      id,
      nominalId: details?.nominalId,
      number: index + 1,
      remainingAmount,
    }),
  );

const getNextCreditRowNumber = items =>
  items.reduce(
    (highestNumber, item) =>
      Math.max(
        highestNumber,
        ...Object.keys(item).map(key => Number(key.match(/\d+/)?.[0] || 0)),
      ),
    0,
  ) + 1;

const buildCreditAllocations = (items, values) =>
  items.map(item => {
    const idField = Object.keys(item).find(key => /^id_\d+$/.test(key));
    const amountField = Object.keys(item).find(key => /^amount_\d+$/.test(key));
    return {
      id: values[idField],
      amount: values[amountField],
    };
  });

const getDefaultCreditReferenceId = ({
  invoiceItems = [],
  selectedIds = [],
}) => {
  const sales = invoiceItems.filter(item => item?.typeId === 8);
  if (sales.length !== 1 || selectedIds.includes(sales[0]?.id)) {
    return null;
  }
  return sales[0]?.id ?? null;
};

export {
  allocateAvailableInvoiceBalance,
  buildCreditAllRows,
  buildCreditAllocations,
  buildCreditRow,
  getDefaultCreditAllocation,
  getDefaultCreditReferenceId,
  getCreditAllSourceItems,
  getNextCreditRowNumber,
};
